import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import recetaService, { RecetaListItemDto } from '../../services/recetaService';

interface Props {
  open: boolean;
  title?: string;
  initialSearchTerm?: string;
  onClose: () => void;
  onSelect: (item: RecetaListItemDto) => void;
}

const PAGE_SIZE = 10;

const RecetaSearchDialog: React.FC<Props> = ({
  open,
  title = 'Buscar receta',
  initialSearchTerm = '',
  onClose,
  onSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [items, setItems] = useState<RecetaListItemDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await recetaService.getRecetas({
        searchTerm: searchTerm.trim() || undefined,
        pageNumber: page + 1,
        pageSize: PAGE_SIZE,
      });
      setItems(res.items ?? []);
      setTotalCount(res.totalCount ?? 0);
    } catch {
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, page]);

  useEffect(() => {
    if (!open) return;
    setSearchTerm(initialSearchTerm);
    setPage(0);
  }, [open, initialSearchTerm]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => void load(), searchTerm ? 250 : 0);
    return () => clearTimeout(t);
  }, [open, load, searchTerm, page]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" component="span">
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            fullWidth
            autoFocus
            label="Buscar por código o descripción"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void load();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : undefined,
            }}
          />

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Producto terminado</TableCell>
                  <TableCell align="right">Componentes</TableCell>
                  <TableCell align="center" width={120}>
                    Acción
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.codigoReceta} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.codigoReceta}</TableCell>
                    <TableCell>{r.descripcion}</TableCell>
                    <TableCell>
                      {r.codigoProductoTerminado
                        ? `${r.codigoProductoTerminado} — ${r.descripcionProductoTerminado ?? ''}`
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{r.cantidadComponentes}</TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          onSelect(r);
                          onClose();
                        }}
                      >
                        Seleccionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Box sx={{ py: 3, color: 'text.secondary' }}>No se encontraron recetas.</Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            labelRowsPerPage="Filas"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecetaSearchDialog;
